import React from "react";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import {
  adaptLiveParticipant,
  formatLongDate,
  formatPersonName,
  formatTimeRange,
  participantNameList,
} from "../utils/signalAdapter";
import CareWatchExtraParticipantsCount from "./CareWatchExtraParticipantsCount";
import { liveShiftTimeLabel, isUnscheduledLiveShift } from "../utils/constants";
import CareWatchLiveStatusBadge from "./CareWatchLiveStatusBadge";
import CareWatchUnscheduledBadge from "./CareWatchUnscheduledBadge";
import { IconX } from "./CareWatchIcons";
import { CareWatchLiveDetailSkeleton } from "./CareWatchSkeletons";
import FloatingPopover, {
  FLOATING_POPOVER_MODE,
  FLOATING_POPOVER_PLACEMENT,
} from "components/common/FloatingPopover";

const workerNameOf = (item) => {
  const ref = item?.workerRef;
  const fromRef = formatPersonName(
    `${ref?.firstName || ""} ${ref?.lastName || ""}`.trim()
  );
  if (fromRef) return fromRef;
  return formatPersonName(
    typeof item?.worker === "string" ? item.worker : item?.worker?.name || ""
  );
};

const initialsOf = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "—";
  return `${parts[0][0] || ""}${parts[1] ? parts[1][0] : ""}`.toUpperCase();
};

const Fact = ({ label, children }) => (
  <div>
    <dt>{label}</dt>
    <dd>{children || "—"}</dd>
  </div>
);

const atShiftTime = (date, time, timezone) => {
  if (!time) return null;
  const zone = timezone || "Australia/Melbourne";
  const raw = String(time).trim().replace("T", " ").slice(0, 19);
  const stamp = /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw : `${date || ""} ${raw}`.trim();
  const parsed = moment.tz(stamp, "YYYY-MM-DD HH:mm:ss", zone);
  return parsed.isValid() ? parsed : null;
};

const LATE_GRACE_MS = 0;

const formatDuration = (ms) => {
  const minutes = Math.max(0, Math.round(ms / 60000));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const formatWindowWhen = (value) => {
  if (!value) return "";
  return value.format("D MMM, h:mma");
};

/** Four parts of the roster window, including time past the scheduled end. */
const windowSegments = (start, end, clockIn) => {
  if (!start || !end || !end.isAfter(start)) return null;
  const startMs = start.valueOf();
  const endMs = end.valueOf();
  const nowMs = moment.tz(start.tz()).valueOf();
  const clockMs = clockIn && clockIn.isValid() ? clockIn.valueOf() : null;
  const clocked = clockMs != null;
  const horizon = Math.max(endMs, clocked ? nowMs : endMs);
  const span = horizon - startMs;
  if (span <= 0) return null;

  const lateBy = clocked ? clockMs - startMs : Math.min(nowMs, endMs) - startMs;
  const countLate = lateBy > LATE_GRACE_MS;
  const notStartedEnd = !clocked
    ? Math.min(nowMs, endMs)
    : countLate
      ? Math.min(Math.max(clockMs, startMs), endMs)
      : startMs;
  const progressStart = clocked ? (countLate ? Math.max(startMs, clockMs) : startMs) : startMs;
  const progressEnd = clocked ? Math.min(Math.max(progressStart, nowMs), endMs) : progressStart;
  const remainingStart = Math.min(Math.max(nowMs, progressEnd), endMs);
  const overrunEnd = clocked && nowMs > endMs ? nowMs : endMs;

  const pct = (from, to) => Math.min(100, Math.max(0, ((to - from) / span) * 100));
  const notStarted = pct(startMs, countLate || !clocked ? notStartedEnd : startMs);
  const progress = pct(progressStart, progressEnd);
  const remaining = nowMs < endMs ? pct(remainingStart, endMs) : 0;
  const overrun = pct(endMs, overrunEnd);

  return {
    notStarted,
    progress,
    remaining,
    overrun,
    notStartedLabel: formatDuration((countLate || !clocked ? notStartedEnd : startMs) - startMs),
    progressLabel: formatDuration(progressEnd - progressStart),
    remainingLabel: formatDuration(Math.max(0, endMs - Math.max(nowMs, startMs))),
    overrunLabel: formatDuration(Math.max(0, overrunEnd - endMs)),
    showNotStarted: notStarted > 0,
    showProgress: progress > 0,
    showRemaining: remaining > 0,
    showOverrun: overrun > 0,
  };
};

const placeName = (zone = "") => {
  const city = String(zone).split("/").pop() || zone;
  return city.replace(/_/g, " ");
};

const LiveWindowPopover = ({ windowTitle, zone, startedLabel, segments }) => {
  const rows = [
    segments.showNotStarted && {
      key: "waiting",
      label: "Not started",
      value: segments.notStartedLabel,
      width: segments.notStarted,
    },
    segments.showProgress && {
      key: "worked",
      label: "Worked",
      value: segments.progressLabel,
      width: segments.progress,
    },
    segments.showRemaining && {
      key: "remaining",
      label: "Remaining",
      value: segments.remainingLabel,
      width: segments.remaining,
    },
    segments.showOverrun && {
      key: "overrun",
      label: "Past end",
      value: segments.overrunLabel,
      width: segments.overrun,
    },
  ].filter(Boolean);

  return (
    <div className="care-watch-live-popover">
      <p className="care-watch-live-popover__kicker">
        {placeName(zone)}
      </p>
      <p className="care-watch-live-popover__title">{windowTitle}</p>
      <p className="care-watch-live-popover__clock">
        {startedLabel || "Not started"}
      </p>
      <div className="care-watch-live-popover__bar" aria-hidden="true">
        {rows.map((row) => (
          <span
            key={row.key}
            className={`is-${row.key}`}
            style={{ width: `${row.width}%` }}
          />
        ))}
      </div>
      <dl className="care-watch-live-popover__rows">
        {rows.map((row) => (
          <div key={row.key} className={`is-${row.key}`}>
            <dt>
              <i />
              {row.label}
            </dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const CareWatchLiveDetail = ({ item, onClose, detailLoading = false }) => {
  const navigate = useNavigate();

  const worker = workerNameOf(item) || "—";
  const unscheduled = isUnscheduledLiveShift(item);
  const people = (item.participants || [])
    .map(adaptLiveParticipant)
    .filter(
      (person) => person.firstName || person.lastName || person.first_name || person.last_name
    );
  const isMulti = people.length > 1;
  const participantNames = participantNameList(people);
  const primaryParticipantName = participantNames[0] || "";
  const extraParticipantNames = participantNames.slice(1);
  const shiftTime = unscheduled
    ? liveShiftTimeLabel(item) ||
      formatTimeRange(item.workerStartTime, item.workerEndTime)
    : formatTimeRange(item.workerStartTime, item.workerEndTime) ||
      item.shiftTimeLabel;

  const serviceLabel = item.serviceLabel || "";

  const shiftZone = item.timezone || "Australia/Melbourne";
  const windowStart = atShiftTime(item.shiftDate, item.workerStartTime, shiftZone);
  const windowEnd = atShiftTime(item.shiftDate, item.workerEndTime, shiftZone);
  const clockInAt = atShiftTime(item.shiftDate, item.clockInAt, shiftZone);
  const segments = unscheduled ? null : windowSegments(windowStart, windowEnd, clockInAt);
  const dayLabel =
    item.shiftDate &&
    moment.tz(item.shiftDate, "YYYY-MM-DD", shiftZone).isSame(moment.tz(shiftZone), "day")
      ? "Today"
      : formatLongDate(item.shiftDate) || item.shiftDateLabel || "Today";
  const crossesMidnight =
    windowStart && windowEnd && !windowStart.isSame(windowEnd, "day");
  const windowTitle = unscheduled
    ? "Unscheduled"
    : crossesMidnight
      ? `${formatWindowWhen(windowStart)} – ${formatWindowWhen(windowEnd)}`
      : [dayLabel, shiftTime].filter(Boolean).join("  ·  ");
  const startedLabel = item.clockInLabel ? `Started ${item.clockInLabel}` : "";
  const remainingLabel = item.remainingLabel
    ? String(item.remainingLabel).replace(/\bleft$/, "remaining")
    : "";
  const showParticipantMeta = Boolean(primaryParticipantName);
  const showSubtitle = Boolean(serviceLabel || showParticipantMeta);

  if (detailLoading) {
    return <CareWatchLiveDetailSkeleton />;
  }

  return (
    <aside className="care-watch-detail-panel care-watch-detail-panel--live">
      <header className="care-watch-detail-header">
        <div className="care-watch-detail-head">
          <span className="care-watch-detail-avatar">{initialsOf(worker)}</span>
          <div className="care-watch-detail-head-main">
            <div className="care-watch-detail-title-row">
              <h2 className="care-watch-detail-name">{worker}</h2>
            </div>
            <div className="care-watch-detail-pills">
              <CareWatchLiveStatusBadge state={item.liveState} />
              <CareWatchUnscheduledBadge item={item} />
              {isMulti ? (
                <span className="care-watch-detail-pill care-watch-detail-pill--status">
                  Group shift
                </span>
              ) : null}
            </div>
            {showSubtitle ? (
              <p className="care-watch-detail-meta care-watch-detail-meta--inline">
                {serviceLabel ? <span>{serviceLabel}</span> : null}
                {serviceLabel && showParticipantMeta ? (
                  <span aria-hidden="true">·</span>
                ) : null}
                {showParticipantMeta ? (
                  <span className="care-watch-detail-meta-participants">
                    <span>{primaryParticipantName}</span>
                    {isMulti ? (
                      <CareWatchExtraParticipantsCount
                        count={extraParticipantNames.length}
                        names={extraParticipantNames}
                        tooltipId={`cw-live-extra-${item.id}`}
                      />
                    ) : null}
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
          {onClose ? (
            <button
              type="button"
              className="care-watch-detail-close"
              aria-label="Close detail"
              onClick={onClose}
            >
              <IconX size={16} />
            </button>
          ) : null}
        </div>
      </header>

      <div className="care-watch-detail-divider" aria-hidden="true" />

      <div className="care-watch-detail-body care-watch-detail-scroll">
        <section className="care-watch-live-clock">
          <p className="care-watch-detail-box-title">Shift window</p>
          <p className="care-watch-live-window">{windowTitle}</p>
          {segments ? (
            <FloatingPopover
              mode={FLOATING_POPOVER_MODE.TOOLTIP}
              placement={FLOATING_POPOVER_PLACEMENT.TOP}
              maxWidth={280}
              offset={10}
              contentClassName="!p-0"
              content={
                <LiveWindowPopover
                  windowTitle={windowTitle}
                  zone={shiftZone}
                  startedLabel={startedLabel}
                  segments={segments}
                />
              }
            >
              <div
                className="care-watch-live-progress"
                role="img"
                aria-label="Shift window"
              >
                {segments.showNotStarted ? (
                  <span className="is-waiting" style={{ width: `${segments.notStarted}%` }} />
                ) : null}
                {segments.showProgress ? (
                  <span className="is-worked" style={{ width: `${segments.progress}%` }} />
                ) : null}
                {segments.showRemaining ? (
                  <span className="is-remaining" style={{ width: `${segments.remaining}%` }} />
                ) : null}
                {segments.showOverrun ? (
                  <span className="is-overrun" style={{ width: `${segments.overrun}%` }} />
                ) : null}
              </div>
            </FloatingPopover>
          ) : null}
          {startedLabel || remainingLabel ? (
            <div className="care-watch-live-clock-meta">
              {startedLabel ? (
                <span className="is-started">{startedLabel}</span>
              ) : (
                <span />
              )}
              {remainingLabel ? (
                <span className="is-remain">{remainingLabel}</span>
              ) : null}
            </div>
          ) : null}
        </section>

        <dl className="care-watch-live-facts care-watch-live-facts--rows">
          <Fact label="Participant">
            {participantNames.length ? (
              <span className="care-watch-live-people">
                {participantNames.map((name, index) => (
                  <span key={`${name}-${index}`}>{name}</span>
                ))}
              </span>
            ) : null}
          </Fact>
          <Fact label="Service">{serviceLabel || "—"}</Fact>
          {/* Timesheet note and per-participant notes stay off Live, same as mobile.
          {shiftNotes.map(...)}
          */}
        </dl>
      </div>

      <div className="care-watch-detail-divider" aria-hidden="true" />

      <footer className="care-watch-detail-footer">
        <div className="care-watch-detail-actions">
          <button
            type="button"
            onClick={() => navigate("/rostering/schedule")}
            className="care-watch-detail-btn care-watch-detail-btn--primary"
          >
            View on roster
          </button>
        </div>
      </footer>
    </aside>
  );
};

export default CareWatchLiveDetail;
